import { useCallback } from "react";

import { observer } from "mobx-react-lite";

import { Cursor, usePlaceholderShown } from "@/components/input/Cursor";
import { useCursorContextId } from "@/components/input/CursorContextProvider";
import { commitPendingInput } from "@/components/input/utils/commitPendingInput";
import { useDuplicateNodeHint } from "@/components/input/utils/duplicateNodeAtCursor";
import { cursorIsActive } from "@/models/CursorModel";
import { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import { ComputedResultNodeModel } from "@/models/nodes/ComputedResultNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { TextNodeModel } from "@/models/nodes/TextNodeModel";
import { getIsMobile } from "@/utils/getIsMobile";
import { matchClick } from "@/utils/keyMatcher";
import { twMerge } from "@/utils/twMerge";

import { useNodeStyle } from "./NodeStyleProvider";
import { Placeholder } from "./Placeholder";

import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

type Props = {
  inlineNode: InlineNodeModel;
  index: number;
  className?: string;
  children?: React.ReactNode;
  /**
   * Defines cursor position in flat node.
   * This is relevant for "filler" flat nodes (e.g. fraction numerator/denominator).
   * If "none", cursor is not rendered
   */
  cursorPosition?: "start" | "end" | "none";
  cursorClassName?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const FlatNode = observer(function FlatNode({
  inlineNode,
  index,
  className,
  children,
  cursorPosition,
  cursorClassName,
  ...props
}: Props) {
  const { doc, nodes } = inlineNode;
  const node = index < nodes.length ? nodes[index] : undefined;
  const isMobile = getIsMobile();

  const hasChildren = !!children;
  const isSelectable =
    isMobile || !(node?.is(OperatorSymbolNodeModel) || node?.is(NumberNodeModel));

  const cursorContextId = useCursorContextId();
  const { renderContext } = useNodeStyle();

  const { placeholderShown, placeholderAppearance, placeholderIndex } =
    usePlaceholderShown(inlineNode);

  const { duplicateNode, insertsOnPlainClick, hintProps } = useDuplicateNodeHint(doc, node);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!node) return;

      if (doc.input.isEditing(node) && node.isInlineEditable()) {
        return;
      }

      e.preventDefault();
      if (e.button !== 2) e.stopPropagation();

      doc.cursorContextId = cursorContextId;

      // Right-click leaves the caret and selection alone; the context menu reads the
      // clicked node off `data-math-node-id` and targets it directly.
      if (e.button === 2) {
        if (cursorIsActive(doc.cursor)) {
          const adjustIndex = commitPendingInput(doc);
          doc.editor.resetInput(true);
          doc.cursor.moveTo(doc.cursor.node, adjustIndex(doc.cursor.node, doc.cursor.index), {
            resetSelection: false,
          });
        }
        return;
      }

      if (node.is(ComputedResultNodeModel) && !isMobile) {
        const match = insertsOnPlainClick
          ? matchClick(e, "reference-at-cursor")
          : matchClick(e, "duplicate-at-cursor");
        if (match && duplicateNode()) {
          return;
        }
      }
      // Modifier clicks act on the node itself, so they run ahead of the branches
      // that would move the caret into it or open it for editing.
      else if (matchClick(e, "duplicate-at-cursor") && duplicateNode()) {
        return;
      }

      if (node.is(ComputedReferenceNodeModel) && matchClick(e, "go-to-source")) {
        commitPendingInput(doc);
        doc.editor.resetInput(true);
        doc.computedNodes.goToSource(node);
        return;
      }

      if (!hasChildren) {
        const adjustIndex = commitPendingInput(doc);
        doc.editor.resetInput(true);
        doc.cursor.moveTo(inlineNode, adjustIndex(inlineNode, index));
        doc.selection.startPointerSelection();
        return;
      }

      if (
        hasChildren &&
        doc.editable &&
        node.isEditable &&
        (node.is(NumberNodeModel) || doc.selection.isActive(node)) &&
        !isMobile
      ) {
        doc.selection.resetSelection();
        if (node.isInlineEditable()) {
          doc.input.editNode(node, { left: e.clientX });
          doc.selection.startEditingNodePointerSelect();
        } else {
          doc.input.editNode(node, undefined);
        }
        doc.cursor.moveBeforeNode(node);
        doc.editor.suggestions.mode = "active";
        return;
      }

      commitPendingInput(doc);
      doc.editor.resetInput(true);

      if (node?.is(TextNodeModel)) {
        return;
      }

      const isMetaKey = e.ctrlKey || e.metaKey;

      if (isMetaKey) {
        if (doc.selection.isActive(node)) {
          doc.selection.resetSelection();
          return;
        }
      }

      if (e.shiftKey) {
        doc.selection.expandRangeToNode(node);
        doc.cursor.hideCursor();
        return;
      }

      if (isSelectable || isMetaKey) {
        doc.input.reset(true);
        if (isMobile) {
          if (e.detail === 1) {
            doc.cursor.moveBeforeNode(node, { resetSelection: true });
          } else if (e.detail >= 2) {
            doc.selection.selectNode(node);
            doc.cursor.moveBeforeNode(node, { resetSelection: false });
            doc.cursor.hideCursor();
          }
        } else if (doc.selection.isActive(node)) {
          doc.selection.resetSelection();
        } else {
          doc.selection.selectNode(node);
          doc.cursor.moveBeforeNode(node, { resetSelection: false });
          doc.cursor.hideCursor();
        }
      } else {
        const rect = e.currentTarget.getBoundingClientRect();
        const shouldMoveBeforeNode = e.clientX < rect.left + rect.width / 2;

        if (shouldMoveBeforeNode) {
          doc.cursor.moveBeforeNode(node);
        } else {
          doc.cursor.moveAfterNode(node);
        }
      }

      doc.editor.hints.setPhantomHints([]);
      doc.selection.startPointerSelection();
      doc.editor.suggestions.mode = "idle";
    },
    [
      hasChildren,
      cursorContextId,
      doc,
      index,
      inlineNode,
      node,
      isSelectable,
      isMobile,
      duplicateNode,
      insertsOnPlainClick,
    ],
  );

  let hideCursor = renderContext !== "editor" && renderContext !== "chat-input";

  if (!hideCursor && inlineNode.ancestors.some((ancestor) => ancestor.isAtom)) {
    hideCursor = true;
  }

  if (hideCursor) {
    return (
      <div
        className={twMerge(
          "relative inline-flex flex-nowrap justify-center items-center",
          className,
        )}
      >
        {children}
        {placeholderShown && (
          <div className="flex items-center before:content-zero-space">
            <Placeholder filled={placeholderAppearance === "filled"} index={placeholderIndex} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-math-node-id={node?.id}
      className={twMerge(
        "relative inline-flex flex-nowrap justify-center items-baseline cursor-text",
        isSelectable && "cursor-pointer",
        className,
      )}
      onMouseDown={handleMouseDown}
      {...hintProps}
      {...props}
    >
      {cursorPosition !== "none" && (
        <Cursor
          node={inlineNode}
          index={index}
          position={cursorPosition}
          className={cursorClassName}
        />
      )}
      {children}
    </div>
  );
});
